// Phase 11 — Goals route with progress bars and milestones.
import * as React from 'react';
import { useGoals } from '../../hooks/useGoals.js';
import { Card, CardContent } from '../../components/ui/Card.js';
import { SkeletonRow } from '../../components/ui/Skeleton.js';
import { EmptyState } from '../../components/ui/EmptyState.js';
import { Button } from '../../components/ui/Button.js';

const STATUS_LABELS: Record<string, string> = { active: 'Active', completed: 'Done', paused: 'Paused' };
const STATUS_COLORS: Record<string, string> = { active: 'bg-accent-subtle text-accent-default', completed: 'bg-state-success text-text-on-accent', paused: 'bg-surface-raised text-text-secondary border-border-default' };

export function Goals(): JSX.Element {
  React.useEffect(() => { document.title = 'DailyDeck — Goals'; }, []);
  const query = useGoals();

  return (
    <div className="space-y-6" data-testid="goals-route">
      <header>
        <h1 className="text-title-1 font-semibold text-text-primary">Goals</h1>
        <p className="text-body text-text-secondary mt-1">Long-term goals with milestones and progress tracking.</p>
      </header>

      {query.isPending && <div className="rounded-lg border border-border-subtle bg-surface-raised"><SkeletonRow /><SkeletonRow /></div>}
      {query.isError && <Card><CardContent className="py-4"><p className="text-body-strong text-text-primary">Could not load goals</p><Button variant="secondary" className="mt-3" onClick={() => void query.refetch()}>Retry</Button></CardContent></Card>}
      {query.isSuccess && query.data.length === 0 && <EmptyState title="No goals yet" description="Set a long-term goal to get started." />}
      {query.isSuccess && query.data.length > 0 && (
        <div className="space-y-4">
          {query.data.map((g) => (
            <Card key={g.id}>
              <CardContent className="py-4 space-y-3">
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <h3 className="text-body-strong font-medium text-text-primary">{g.title}</h3>
                      <span className={`inline-flex rounded-full px-2 py-0.5 text-micro font-medium border ${STATUS_COLORS[g.status] ?? ''}`}>{STATUS_LABELS[g.status] ?? g.status}</span>
                    </div>
                    {g.description && <p className="text-caption text-text-secondary mt-1">{g.description}</p>}
                  </div>
                  {g.target_date && <span className="text-caption text-text-muted tabular-nums shrink-0">{g.target_date}</span>}
                </div>
                <div className="flex items-center gap-3">
                  <div className="flex-1 h-2 rounded-full bg-surface-base overflow-hidden">
                    <div className="h-full rounded-full bg-accent-default transition-all" style={{ width: `${g.progress_pct}%` }} />
                  </div>
                  <span className="text-micro text-text-muted tabular-nums w-10 text-right">{g.progress_pct}%</span>
                </div>
                {g.milestones && g.milestones.length > 0 && (
                  <div className="space-y-1 pt-1 border-t border-border-subtle">
                    {g.milestones.map((m) => (
                      <div key={m.id} className="flex items-center gap-2 text-caption">
                        <span className={m.completed ? 'text-state-success' : 'text-text-muted'}>{m.completed ? '✓' : '○'}</span>
                        <span className={m.completed ? 'text-text-muted line-through' : 'text-text-secondary'}>{m.title}</span>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
